import React, { useState } from 'react';
import StatusBadge from '../common/StatusBadge';
import { formatDateTime, maskIdNumber } from '../../utils/formatters';
import { CONSENT_CHECK_TYPES } from '../../utils/constants';
import './ApplicationDetail.css';

const ApplicationDetail = ({ application, consents = [], documents = [] }) => {
  const [activeTab, setActiveTab] = useState('summary');

  if (!application) return null;

  const renderSummary = () => (
    <div className="detail-section">
      <h3>Applicant Summary</h3>
      <dl className="info-grid">
        <div className="info-item">
          <dt>Name</dt>
          <dd>{application.firstName} {application.lastName}</dd>
        </div>
        <div className="info-item">
          <dt>ID Number</dt>
          <dd>{maskIdNumber(application.idNumber)}</dd>
        </div>
        <div className="info-item">
          <dt>Gross Income</dt>
          <dd>R {application.grossIncome}</dd>
        </div>
        <div className="info-item">
          <dt>Employment</dt>
          <dd>{application.jobTitle} at {application.employerName}</dd>
        </div>
      </dl>

      <h3 style={{ marginTop: '24px' }}>Consent Status</h3>
      <div className="consent-summary-grid">
        {Object.entries(CONSENT_CHECK_TYPES).map(([key, typeInfo]) => {
          const consentRecord = consents.find(c => c.type === key);
          const isGranted = consentRecord?.granted && !consentRecord?.withdrawn;
          return (
            <div key={key} className="consent-summary-item">
              <span>{typeInfo.label}</span>
              <StatusBadge status={isGranted ? 'GRANTED' : 'REFUSED'} />
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderDocuments = () => (
    <div className="detail-section">
      <h3>Submitted Documents</h3>
      {documents.length > 0 ? (
        <ul className="doc-list">
          {documents.map(doc => (
            <li key={doc.id} className="doc-item">
              <span className="doc-type">{doc.type}</span>
              <span className="doc-name">{doc.filename}</span>
              <StatusBadge status={doc.status} />
              <button className="btn-action">View</button>
            </li>
          ))}
        </ul>
      ) : (
        <p>No documents uploaded yet.</p>
      )}
    </div>
  );

  const renderVerificationPlaceholder = () => (
    <div className="detail-section verification-placeholder">
      <h3>Verification Results (Phase 3)</h3>
      <p>Verification engine integration will display background check results here.</p>
      <button className="btn-primary" disabled>Run Verification</button>
    </div>
  );

  return (
    <div className="application-detail">
      <div className="application-detail__header card">
        <div className="card-body header-content">
          <div>
            <h1>Application for {application.propertyAddress}</h1>
            <p className="header-meta">
              Applicant: {application.firstName} {application.lastName} | Submitted: {formatDateTime(application.submittedAt)}
            </p>
          </div>
          <div className="header-status">
            <StatusBadge status={application.status} />
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-secondary">Request More Info</button>
          {application.status === 'SUBMITTED' && <button className="btn-primary">Start Verification</button>}
        </div>
      </div>

      <div className="application-detail__tabs">
        <button 
          className={`tab-btn ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          Profile & Consents
        </button>
        <button 
          className={`tab-btn ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          Documents
        </button>
        <button 
          className={`tab-btn ${activeTab === 'verification' ? 'active' : ''}`}
          onClick={() => setActiveTab('verification')}
        >
          Verification
        </button>
      </div>

      <div className="application-detail__content card">
        <div className="card-body">
          {activeTab === 'summary' && renderSummary()}
          {activeTab === 'documents' && renderDocuments()}
          {activeTab === 'verification' && renderVerificationPlaceholder()}
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetail;
